import React from 'react';

export default function CompliancePage({ onAuditClick }) {
    return (
        <div className="p-10 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-7xl mx-auto">
            {/* Page Header */}
            <div>
                <h1 className="text-4xl font-black text-espresso tracking-tighter uppercase mb-2">
                    Compliance Intelligence
                </h1>
                <p className="text-xs font-bold text-teal tracking-[0.3em] uppercase opacity-60">
                    Risk Mitigation & Legal Protection
                </p>
            </div>

            {/* Top Bento Grid - Reality Check */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bento-card p-8 bg-espresso text-white shadow-xl shadow-espresso/10 outline m-1 outline-4 outline-espresso/5">
                    <h2 className="text-[0.625rem] font-black uppercase tracking-widest text-white/50 mb-6">Annual Lawsuits</h2>
                    <div className="flex items-baseline gap-2 mb-4">
                        <div className="text-5xl font-black tracking-tighter">4,000</div>
                        <div className="text-2xl font-black text-teal">+</div>
                    </div>
                    <p className="text-xs font-bold opacity-80 leading-relaxed uppercase tracking-widest">
                        Digital accessibility lawsuits filed in US courts last year alone, targeting businesses of all sizes.
                    </p>
                </div>
                <div className="bento-card p-8 bg-white border border-espresso/10">
                    <h2 className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/40 mb-6">Failure Rate</h2>
                    <div className="text-5xl font-black text-(--fail-red) mb-4 tracking-tighter">97%</div>
                    <p className="text-xs font-bold text-espresso/60 leading-relaxed uppercase tracking-widest">
                        Of the top 1 million websites fail basic accessibility tests, leaving them exposed to aggressive litigation.
                    </p>
                </div>
                <div className="bento-card p-8 bg-teal text-white shadow-xl shadow-teal/20 outline m-1 outline-4 outline-teal/10">
                    <h2 className="text-[0.625rem] font-black uppercase tracking-widest text-white/60 mb-6">Market Exclusion</h2>
                    <div className="text-5xl font-black mb-4 tracking-tighter">25%</div>
                    <p className="text-xs font-bold opacity-90 leading-relaxed uppercase tracking-widest">
                        Of the population lives with a disability. Ensuring compliance means recapturing a massive lost demographic.
                    </p>
                </div>
            </div>

            {/* Middle Grid - What We Scan For & CTA */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Audit CTA */}
                <div className="bento-card p-8 bg-stone/50 border-2 border-teal/20 flex flex-col justify-between">
                    <div>
                        <div className="w-12 h-12 bg-teal/10 text-teal rounded-2xl flex items-center justify-center text-xl mb-6 shadow-inner">🛡️</div>
                        <h3 className="text-2xl font-black uppercase tracking-tight text-espresso mb-4">
                            Protect Your Business
                        </h3>
                        <p className="text-[0.625rem] font-bold text-espresso/60 leading-relaxed uppercase tracking-widest mb-8">
                            Identify your active liabilities immediately. Generate a comprehensive legal risk report for your domains.
                        </p>
                    </div>
                    <button
                        onClick={onAuditClick}
                        className="w-full py-4 bg-teal text-white rounded-xl font-black text-[0.625rem] uppercase tracking-widest shadow-lg shadow-teal/30 hover:shadow-teal/50 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
                    >
                        <span>📊</span> Generate Risk Report
                    </button>
                </div>

                {/* Scan parameters */}
                <div className="lg:col-span-2 bento-card p-8 bg-white border border-espresso/10">
                    <h2 className="text-xs font-black uppercase tracking-widest text-espresso mb-8">Audit Parameters</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[
                            { icon: "⌨️", title: "Navigation Traps", desc: "Detecting keyboard-only roadblocks that prevent full site traversal." },
                            { icon: "👁️", title: "Contrast Failures", desc: "Validating text/background contrast ratios against WCAG AA/AAA standards." },
                            { icon: "🖼️", title: "Missing Semantics", desc: "Identifying missing alt-text and ARIA labels critical for screen readers." },
                            { icon: "📑", title: "Document Hierarchy", desc: "Ensuring logical heading structures and landmark regions for clear parsing." }
                        ].map((item, i) => (
                            <div key={i} className="flex gap-4 p-5 rounded-3xl bg-stone/30 hover:bg-stone transition-colors border border-transparent hover:border-espresso/5 group">
                                <div className="w-12 h-12 bg-white shadow-sm border border-espresso/5 rounded-2xl flex items-center justify-center text-xl shrink-0 group-hover:scale-110 transition-transform">
                                    {item.icon}
                                </div>
                                <div>
                                    <h3 className="text-[0.625rem] font-black uppercase tracking-widest text-espresso mb-2">{item.title}</h3>
                                    <p className="text-[0.5625rem] font-bold text-espresso/50 leading-relaxed uppercase tracking-widest">
                                        {item.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Premium Service */}
            <div className="bento-card p-12 bg-gradient-to-br from-espresso via-espresso to-black text-white overflow-hidden relative border-none shadow-2xl">
                <div className="absolute top-0 right-0 w-[31.25em] h-[31.25em] bg-teal/20 blur-[7.5em] rounded-full translate-x-1/3 -translate-y-1/3 mix-blend-screen pointer-events-none"></div>
                <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="inline-block px-4 py-1.5 bg-teal/20 border border-teal/30 rounded-full text-[0.5625rem] font-black uppercase tracking-widest text-teal mb-8 shadow-inner shadow-teal/20">
                            Concierge Remediation
                        </div>
                        <h3 className="text-4xl font-black tracking-tighter mb-6 leading-tight">
                            Guaranteed Compliance. <br/>Zero Engineering Hours.
                        </h3>
                        <p className="text-[0.625rem] font-bold text-white/60 leading-relaxed uppercase tracking-widest mb-10 max-w-md">
                            Our "Done-For-You" managed service shifts liability completely. Our engineering experts will resolve every WCAG failure directly in your codebase.
                        </p>
                        <button className="px-8 py-5 bg-white text-espresso rounded-full font-black text-[0.625rem] uppercase tracking-widest hover:bg-teal hover:text-white transition-all shadow-xl hover:shadow-teal/20 hover:-translate-y-0.5">
                            Schedule Technical Consultation
                        </button>
                    </div>
                    <div className="hidden lg:grid grid-cols-2 gap-6 relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent z-0 rounded-3xl mix-blend-overlay"></div>
                        <div className="bg-white/5 p-8 rounded-3xl border border-white/10 backdrop-blur-xl shadow-2xl z-10 hover:bg-white/10 transition-colors">
                            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-2xl mb-6 shadow-inner">👩‍💻</div>
                            <div className="text-[0.6875rem] font-black uppercase tracking-widest mb-2 text-white">Code Fixes</div>
                            <div className="text-[0.5625rem] font-bold text-teal uppercase tracking-widest">Direct PR implementation</div>
                        </div>
                        <div className="bg-white/5 p-8 rounded-3xl border border-white/10 backdrop-blur-xl shadow-2xl z-10 translate-y-8 hover:bg-white/10 transition-colors">
                            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-2xl mb-6 shadow-inner">⚖️</div>
                            <div className="text-[0.6875rem] font-black uppercase tracking-widest mb-2 text-white">Legal Protection</div>
                            <div className="text-[0.5625rem] font-bold text-teal uppercase tracking-widest">Certification letters given</div>
                        </div>
                    </div>
                </div>
            </div>
            
        </div>
    );
}
